import clsxm from '@/lib/clsxm';

type HalftoneProps = {
  className?: string;
  /** Where the tone is darkest, as a CSS position (e.g. '100% 0%'). */
  at?: string;
  /**
   * Dot colour. Keep it opaque and set the strength with an opacity class.
   * Defaults to the `--ht` custom property so a theme class can swap it.
   */
  color?: string;
  /** Distance between neighbouring dots, in px. */
  spacing?: number;
  /** Radius of the dots at the focal point, in px. */
  maxRadius?: number;
  /** Where the tone runs out, in % of the distance to the farthest corner. */
  reach?: number;
  steps?: number;
};

/**
 * A print-style halftone: dots on a 45-degree screen that shrink with
 * distance from a focal point. Built from stacked CSS layers, one per dot
 * size, each masked to a disc around the focal point, so the texture stays
 * crisp at any pixel density and costs no image request.
 *
 * The dots of every layer share one grid, so where layers overlap the larger
 * dot simply covers the smaller one. Colours must be opaque for that reason.
 */
export default function Halftone({
  className,
  at = '100% 0%',
  color = 'var(--ht, currentColor)',
  spacing = 14,
  maxRadius = 5.6,
  reach = 85,
  steps = 10,
}: HalftoneProps) {
  // Two dots per tile, on the diagonal: neighbours sit `spacing` apart.
  const tile = +(spacing * Math.SQRT2).toFixed(2);
  const soft = 2.5;

  const layers = Array.from({ length: steps }, (_, index) => {
    // Dot area follows the tone, so the radius falls off like a square root.
    const tone = 1 - index / steps;
    const radius = +(maxRadius * Math.sqrt(tone)).toFixed(2);
    const edge = +((reach * (index + 1)) / steps).toFixed(2);
    return { radius, edge };
  }).filter((layer) => layer.radius >= 0.6);

  return (
    <div
      aria-hidden='true'
      className={clsxm('pointer-events-none absolute inset-0', className)}
    >
      {layers.map(({ radius, edge }) => {
        const dot = `${color} ${radius}px, transparent ${radius + 0.7}px`;
        const mask = `radial-gradient(circle farthest-corner at ${at}, #000 ${Math.max(
          edge - soft,
          0,
        )}%, transparent ${edge}%)`;
        return (
          <span
            key={edge}
            className='absolute inset-0'
            style={{
              backgroundImage: `radial-gradient(circle at 25% 25%, ${dot}), radial-gradient(circle at 75% 75%, ${dot})`,
              backgroundSize: `${tile}px ${tile}px`,
              WebkitMaskImage: mask,
              maskImage: mask,
            }}
          />
        );
      })}
    </div>
  );
}
